@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.browser.http.http
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.dom.hasClass
import kotlinx.dom.removeClass
import org.jetbrains.compose.web.renderComposable
import org.w3c.dom.EventSource
import org.w3c.dom.EventSourceInit
import org.w3c.dom.MessageEvent
import org.w3c.dom.`get`
import app.spliddy.web.components.layouts.PageLayout as _app_spliddy_web_components_layouts_PageLayout
import app.spliddy.web.pages.HomePage as _app_spliddy_web_pages_HomePage
import app.spliddy.web.pages.ImprintPage as _app_spliddy_web_pages_ImprintPage
import app.spliddy.web.pages.InstallPage as _app_spliddy_web_pages_InstallPage
import app.spliddy.web.pages.PrivacyPolicyPage as _app_spliddy_web_pages_PrivacyPolicyPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

private fun forceReloadNow() {
    window.stop()
    window.location.reload()
}

private fun handleServerStatusEvents() {
    val status = document.getElementById("status")!!
    var lastVersion: Int? = null
    var shouldReload = false

    val warningIcon = status.children[0]!!
    val spinnerIcon = status.children[1]!!
    val statusText = status.children[2]!!

    status.addEventListener("transitionend", {
        if (status.hasClass("fade-out")) {
            status.removeClass("fade-out")
            if (shouldReload) {
                forceReloadNow()
            }
        }
    })

    val eventSource = EventSource("/api/kobweb-status", EventSourceInit(true))
    eventSource.addEventListener("version", { evt ->
        val version = (evt as MessageEvent).data.toString().toInt()
        if (lastVersion == null) {
            lastVersion = version
        }
        if (lastVersion != version) {
            lastVersion = version
            if (document.asDynamic().hidden) {
                // Reload immediately when the page is not visible as the animation will not run
                forceReloadNow()
            } else if (status.className.isNotEmpty()) {
                shouldReload = true
            } else {
                // Not sure if we can get here but if we can't rely on the status transition
                // to reload we should do it ourselves.
                forceReloadNow()
            }
        }
    })

    eventSource.addEventListener("status", { evt ->
        val values: dynamic = JSON.parse<Any>((evt as MessageEvent).data.toString())
        val text = values.text as String
        val isError = (values.isError as String).toBoolean()
        if (text.isNotBlank()) {
            warningIcon.className = if (isError) "visible" else "hidden"
            spinnerIcon.className = if (isError) "hidden" else "visible"
            statusText.innerHTML = "<i>$text</i>"
            status.className = "fade-in"
        } else {
            if (status.className == "fade-in") {
                status.className = "fade-out"
            }
        }
    })

    eventSource.onerror = { eventSource.close() }
}

public fun main() {
    handleServerStatusEvents()

    window.http.logOnError = true

    AppGlobals.initialize(mapOf("title" to "Spliddy"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "app.spliddy.web.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _app_spliddy_web_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "app.spliddy.web.components.layouts.PageLayout",
        ) { pageCtx -> 
            _app_spliddy_web_pages_HomePage()
        }
        ctx.router.register(
            "/imprint",
            layoutId = "app.spliddy.web.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                app.spliddy.web.pages.initImprintPagePage(ctx)
            },
        ) { pageCtx -> 
            _app_spliddy_web_pages_ImprintPage()
        }
        ctx.router.register(
            "/install",
            layoutId = "app.spliddy.web.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                app.spliddy.web.pages.initInstallPagePage(ctx)
            },
        ) { pageCtx -> 
            _app_spliddy_web_pages_InstallPage()
        }
        ctx.router.register(
            "/privacy-policy",
            layoutId = "app.spliddy.web.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                app.spliddy.web.pages.initPrivacyPolicyPagePage(ctx)
            },
        ) { pageCtx -> 
            _app_spliddy_web_pages_PrivacyPolicyPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", app.spliddy.web.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", app.spliddy.web.SubheadlineTextStyle)
        ctx.theme.registerStyle("page-content", app.spliddy.web.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("footer", app.spliddy.web.components.sections.FooterStyle)
        ctx.theme.registerStyle("footer-container", app.spliddy.web.components.sections.FooterContainerStyle)
        ctx.theme.registerStyle("nav-header", app.spliddy.web.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("hero-container", app.spliddy.web.pages.HeroContainerStyle)
        ctx.theme.registerStyle("app-demo-image", app.spliddy.web.pages.AppDemoImageStyle)
        ctx.theme.registerVariant("-circle", app.spliddy.web.CircleButtonVariant)
        ctx.theme.registerVariant("-uncolored", app.spliddy.web.UncoloredButtonVariant)
        app.spliddy.web.initTheme(ctx)
        app.spliddy.web.initLanguage(ctx)
        app.spliddy.web.initColorMode(ctx)
        app.spliddy.web.initStyles(ctx)
        app.spliddy.web.initSiteStyles(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        app.spliddy.web.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
